<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Directorships
              <tooltip
                v-if="directorshipTooltipText !== ''"
                :content="directorshipTooltipText"
                :tooltip-options="{
                  classes: ['large'],
                }"
              />
            </h2>
            <div class="subtitle is-2">
              <div v-html="directorshipText ? directorshipText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Directorships</strong> to report.
            </checkbox-adv>
          </div>
        </div>
        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-directorships"
            tag="form"
          >
            <div class="columns is-multiline is-vcentered">
              <div class="column is-5">
                <dropdown-vee
                  id="highest-position"
                  v-model="newItem.positiontype"
                  :options="positionTypes"
                  placeholder="Highest Position"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-7">
                <textbox-vee
                  id="organization-name"
                  v-model="newItem.name"
                  placeholder="Organization Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-5">
                <textbox-vee
                  id="position"
                  v-model="newItem.position"
                  placeholder="Position"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="organization-desc"
                  v-model="newItem.nature"
                  placeholder="Organization Description"
                />
              </div>
              <div class="column is-3">
                <checkbox-adv
                  id="not-for-profit"
                  v-model="newItem.nonprofit"
                  :value="'Y'"
                  :true-value="'Y'"
                  :false-value="'N'"
                >
                  Not for Profit
                </checkbox-adv>
              </div>
              <div class="column is-4">
                <textbox
                  id="organization-address"
                  v-model="newItem.streetAddress"
                  placeholder="Organization Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="organization-city"
                  v-model="newItem.city"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="zip-code"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
              <div class="column is-12 has-text-centered">
                <div class="column">
                  <button
                    id="action-btn"
                    type="button"
                    class="button is-secondary"
                    @click="trigerNewItemAction"
                  >
                    {{ tableAction }} Directorship
                  </button>
                </div>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinueCheck('Directorships')"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import Tooltip from '@/components/phila-ui/Tooltip';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'Directorships',
  components: {
    CheckboxAdv,
    TextboxVee,
    Textbox,
    Dropdown,
    DropdownVee,
    Tooltip,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data () {
    return {
      currentFiling: this.getCurrentFiling(),
      items: this.getCurrentDirectorships(),
      isSaving: false,
      newItem: {
        positionType: '',
        name: '',
        position: '',
        nonProfit: null,
        nature: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        person: '',
      },
      nothingToReport: false,
      table: [
        'position',
        'name',
        'streetAddress',
        'edit',
        'clone',
        'delete',
      ],
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        filterable: false,
        perPage: 50,
        perPageValues: [],
        headings: {
          name: "Organization",
          streetAddress: "Address",
        },
      },
    };
  },
  computed: {
    positionTypes () {
      return this.controlValueDropdown('positiontype');
    },
    states () {
      return this.controlValueDropdown('state');
    },
    directorshipText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('directorshipText');
    },
    directorshipTooltip () {
      return this.$store.getters[ 'admin/activeControlValues' ]('directorshipTooltip');
    },
    directorshipTooltipText () {
      let tooltip = [];
      if (this.currentFiling && this.directorshipTooltip) {
        if (this.currentFiling.stateForm === 'Y' || this.currentFiling.cityForm === 'Y') {
          tooltip.push(this.directorshipTooltip[0].description);
        }
        if (this.currentFiling.mayorForm === 'Y') {
          tooltip.push(this.directorshipTooltip[1].description);
        }
      }
      return tooltip.join('<br>');
    },
    familyRelationships () {
      return this.controlValueDropdown('familymember');
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentDirectorships();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'directorshipText'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'directorshipTooltip'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'positiontype'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'familymember'));
      promises.push(this.$store.dispatch('directorships/fetchDirectorships'));
      return Promise.all(promises);
    },
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.directorshipsCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentDirectorships () {
      return this.cloneObj(this.$store.state.directorships.directorships);
    },
    saveAndContinue () {
      let promises = [];

      const col = 'directorshipsCompleted';
      const endpoint = 'directorships';

      let filing = {
        id: this.currentFiling.id,
      };

      filing[col] = this.currentFiling[col];

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-directorships'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
  },
};
</script>
